'use client';

import React, { useState } from 'react';

import Icon from '@/components/ui/AppIcon';

import ContactInfo from './ContactInfo';

import LocationMap from './LocationMap';

import FAQSection from './FAQSection';

import NewsletterSignup from './NewsletterSignup';
import { trackLeadFormSubmission } from '@/lib/seo/conversionTracking';

export default function ContactUsInteractive() {
  const [formData, setFormData] = useState({
    name: '',

    email: '',

    phone: '',

    message: '',

    preferredPuppy: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify({
          ...formData,

          formType: 'contact',
        }),
      });

      if (response.ok) {
        trackLeadFormSubmission({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          puppyType: formData.preferredPuppy,
          leadSource: 'contact_page_form',
        });

        setSubmitStatus('success');

        setFormData({ name: '', email: '', phone: '', message: '', preferredPuppy: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);

      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-12">
      {/* Main Contact Section */}

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Contact Form */}

        <div className="lg:col-span-2">
          <div className="rounded-xl bg-white p-8 shadow-warm-lg">
            <h2 className="font-heading text-3xl font-bold text-foreground mb-2">Get in Touch</h2>

            <p className="text-muted-foreground mb-6">
              Fill out the form below and we'll get back to you within 24 hours. We're excited to
              help you find your perfect Labrador puppy!
            </p>

            {submitStatus === 'success' && (
              <div className="mb-6 flex items-start gap-3 rounded-md bg-success/10 p-4 text-success border border-success/20">
                <Icon name="CheckCircleIcon" size={20} variant="solid" />

                <p className="text-sm font-medium">
                  Thank you! Your message has been sent successfully. We'll get back to you within
                  24 hours.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 flex items-start gap-3 rounded-md bg-destructive/10 p-4 text-destructive border border-destructive/20">
                <Icon name="XCircleIcon" size={20} variant="solid" />

                <p className="text-sm font-medium">
                  Sorry, there was an error sending your message. Please try again or email us
                  directly at luminexlabradors@gmail.com
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>

                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-border bg-background rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>

                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-border bg-background rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  Phone Number
                </label>

                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-border bg-background rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="puppy" className="block text-sm font-medium text-foreground mb-2">
                  Interested Puppy (Optional)
                </label>

                <select
                  id="puppy"
                  value={formData.preferredPuppy}
                  onChange={(e) => setFormData({ ...formData, preferredPuppy: e.target.value })}
                  className="w-full px-4 py-3 border border-border bg-background rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select a puppy (optional)</option>

                  <option value="FINN - Yellow Labrador Male">FINN - Yellow Labrador Male</option>

                  <option value="JAX - Black Labrador Male">JAX - Black Labrador Male</option>

                  <option value="CoCo - Yellow Labrador Female">
                    CoCo - Yellow Labrador Female
                  </option>

                  <option value="LuLu - Chocolate Labrador Female">
                    LuLu - Chocolate Labrador Female
                  </option>

                  <option value="Bonley - Chocolate Labrador Male">
                    Bonley - Chocolate Labrador Male
                  </option>

                  <option value="LiLi - Yellow Labrador Female">
                    LiLi - Yellow Labrador Female
                  </option>

                  <option value="BRUNO - Yellow Labrador Male">BRUNO - Yellow Labrador Male</option>

                  <option value="WALTERS - Chocolate Labrador Male">
                    WALTERS - Chocolate Labrador Male
                  </option>

                  <option value="Not sure yet - Help me choose">
                    Not sure yet - Help me choose
                  </option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Message *
                </label>

                <textarea
                  id="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border border-border bg-background rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Tell us about your inquiry..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-white py-3 px-6 rounded-md font-semibold hover:bg-primary/90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        {/* Contact Information Sidebar */}

        <div className="space-y-6">
          <ContactInfo />

          <NewsletterSignup />
        </div>
      </div>

      {/* Location Map */}

      <LocationMap />

      {/* FAQ Section */}

      <FAQSection />
    </div>
  );
}
